import { ThunkAction } from 'redux-thunk';
import * as Bixby from './../../core';
import { AppState } from '../index';

import { PROFILE_LOADING, PROFILE_SUCCESS, PROFILE_FAILURE, PROFILE_SET_STATUS } from './types';
import { ProfileService, AnalyticsService, UserAdminService, TokenService } from '$/services';
import { toastError } from '$/store/toast/actions';

// thunk actions
/**
 * Thunk action to get current profile
 */
export const getProfile =
  (): ThunkAction<Promise<Bixby.Models.Profile>, AppState, null, Bixby.Models.BixbyAction> =>
  async (dispatch, getState) => {
    dispatch(loadProfileAction());
    try {
      const profile = ProfileService.getProfileFromState(getState) as Bixby.Models.Profile;
      if (!profile) {
        throw new Error(`Expected profile from decoded token.`);
      }
      dispatch(profileSuccessAction(profile));
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_PROFILE_LOADED, profile);
      return profile;
    } catch (error: any) {
      dispatch(profileFailureAction(error));
      throw error;
    }
  };

/**
 * Load profile action creator
 */
export const loadProfileAction = () => {
  return {
    type: PROFILE_LOADING,
  };
};

/**
 * Successfully loaded profile action creator
 * @param profiles user profile
 */
export const profileSuccessAction = (payload: Bixby.Models.Profile) => {
  return {
    type: PROFILE_SUCCESS,
    payload,
  };
};

/**
 * Unsuccessfully loaded profile action creator
 * @param error error when trying to load user profile
 */
export const profileFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: PROFILE_FAILURE,
    payload: error,
    error: true,
  };
};

const profileSetStatusAction = (status: Bixby.Enums.UserStatus) => {
  return {
    type: PROFILE_SET_STATUS,
    payload: status,
  };
};

export const loadUserStatus =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
    const userId = TokenService.getUserId();
    if (!userId) return;
    try {
      const user = await UserAdminService.getById(userId);
      if (user.status) {
        const normalized = UserAdminService.fromApiStatus(user.status as unknown as string) ?? user.status;
        dispatch(profileSetStatusAction(normalized));
      }
    } catch {
      // status is optional — silently ignore fetch failures
    }
  };

export const updateUserStatus =
  (status: Bixby.Enums.UserStatus): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async (dispatch) => {
    const userId = TokenService.getUserId();
    if (!userId) return;
    try {
      await UserAdminService.updateStatusById(userId, status);
      dispatch(profileSetStatusAction(status));
    } catch {
      dispatch(toastError('Failed to update user status'));
    }
  };
